import { GridState } from "client_legacy/Workspace/gridState";
import {
	PLACEHOLDER_ID,
	PlaceHolderId as PlaceholderId,
	TileData,
	TileGridConfiguration,
	TileGridDimensionLimits,
	TileGridRenderSettings,
	TileGridRenderState,
	TilePlaceHolderRenderState,
	TileRenderState
} from "../common";
import * as tileReducers from "./tile";
import * as tileGridSelectors from "../selectors/tileGrid";
import { InteractionState } from "../../Tiles/TileContext";
import { TopLeftPackingAlgorithm } from "client_legacy/Workspace/Packing/topLeft";
import { TileGridReducerContext, TileGridChangeEffect } from "./effects";
import { GridArea, GridDimension } from "client/Api/Workspace/common";
import { isNotNil } from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { ModifiedCollectionBuilder, setPropertiesPreservingRefWhenUnchanged } from "./common";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { AppType } from "client/Api/Apps/common";

export function generateInitialState(
	context: TileGridReducerContext,
	tiles: readonly TileData[],
	tileInteractionState: InteractionState,
	tileGridRenderSettings: TileGridRenderSettings
): TileGridRenderState {
	const tileRenderStates: readonly TileRenderState[] = tiles.map(
		tile => tileReducers.generateInitialState(
			tile,
			tileInteractionState,
			tileGridRenderSettings.configuration
		)
	);

	const tileGrid: Omit<TileGridRenderState, "dimension" | "settings"> = {
		tiles: tileRenderStates,
		placeholder: undefined
	};

	return applyRenderSettingsInternal(
		tileGrid,
		context,
		tileGridRenderSettings
	);
}

export function applyRenderSettings(
	renderState: TileGridRenderState,
	context: TileGridReducerContext,
	renderSettings: TileGridRenderSettings
): TileGridRenderState {
	return applyRenderSettingsInternal(
		renderState,
		context,
		renderSettings
	);
}

export function applyTileInteractionState(
	currentState: TileGridRenderState,
	context: TileGridReducerContext,
	interactionState: InteractionState
): TileGridRenderState {
	const resultTiles: TileRenderState[] = [];
	let hasChanges: boolean = false;

	for (const currentTileState of currentState.tiles) {
		const resultTileState: TileRenderState = tileReducers.applyNewInteractionState(
			currentTileState,
			interactionState
		);

		resultTiles.push(resultTileState);

		hasChanges ||= currentTileState !== resultTileState;
	}

	if (!hasChanges) {
		return currentState;
	}

	currentState = {
		...currentState,
		tiles: resultTiles
	};

	const gridState: GridState<TileRenderState> = tileGridSelectors
		.generateStateOfTilesConsumingSpace(currentState);

	return applyGridState(
		currentState,
		context,
		gridState
	);
}

export function adjustTilesForNewConfiguration(
	currentState: TileGridRenderState,
	context: TileGridReducerContext,
	configuration: TileGridConfiguration
): TileGridRenderState {
	const updatedTilesBuilder = new ModifiedCollectionBuilder<TileRenderState>(currentState.tiles);

	for (const tile of currentState.tiles) {
		updatedTilesBuilder.push(
			tileReducers.adjustDimensionForNewConfiguration(
				tile,
				configuration
			)
		);
	}

	const packingAlgorithm = new TopLeftPackingAlgorithm(
		configuration.dimensionLimits
			.columns
	);

	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			tiles: updatedTilesBuilder.result()
		}
	);

	const gridState: GridState<TileRenderState> = tileGridSelectors
		.generateStateOfTilesConsumingSpace(
			currentState,
			true // doNotApplyPackingAlgorithm
		);

	gridState.apply(packingAlgorithm);

	currentState = applyGridState(
		currentState,
		context,
		gridState
	);

	return persistTileStates(currentState);
}

export function dropTile(
	currentState: TileGridRenderState,
	context: TileGridReducerContext,
	tile: TileRenderState,
	placementArea: GridArea,
	isSource: boolean
): TileGridRenderState {
	const tileId: number = tile.data.id;
	tile = tileReducers.setIsBeingDragged(tile, false);

	const gridState: GridState<TileRenderState> = tileGridSelectors
		.generateStateOfTilesConsumingSpace(currentState)
		.freeUpArea(placementArea)
		.add({
			...placementArea,
			item: tile
		})
		.apply(
			currentState.settings
				.packingAlgorithm
		);

	const tilesCopy: TileRenderState[] = [
		...currentState.tiles
	];

	if (isSource) {
		const indexOfTile: number = tilesCopy.findIndex(x => x.data.id === tileId);

		if (indexOfTile === -1) {
			throw new Error("Dragged tile is not present in the source.");
		}

		tilesCopy.splice(
			indexOfTile,
			1,
			tile
		);
	} else {
		tilesCopy.push(tile);
	}

	currentState = {
		...currentState,
		tiles: tilesCopy
	};

	currentState = applyGridState(
		currentState,
		context,
		gridState
	);

	return persistTileStates(currentState);
}

export function removeTile(
	currentState: TileGridRenderState,
	context: TileGridReducerContext,
	tileId: number
): TileGridRenderState {
	const tileCollectionBuilder = new ModifiedCollectionBuilder<TileRenderState>(currentState.tiles);

	tileCollectionBuilder.remove(t => t.data.id === tileId);

	const updatedTiles: readonly TileRenderState[] = tileCollectionBuilder.result();

	if (currentState.tiles === updatedTiles) {
		return currentState;
	}

	currentState = {
		...currentState,
		tiles: updatedTiles
	};

	const gridState: GridState<TileRenderState> = tileGridSelectors
		.generateStateOfTilesConsumingSpace(currentState);

	currentState = applyGridState(
		currentState,
		context,
		gridState
	);

	return persistTileStates(currentState);
}

export function resizeTile(
	currentState: TileGridRenderState,
	context: TileGridReducerContext,
	tileId: number,
	dimension: GridDimension
): TileGridRenderState {
	const maxColumns: number = currentState
		.settings
		.configuration
		.dimensionLimits
		.columns;

	if (dimension.columnSpan > maxColumns) {
		throw new Error(`Area with column span ${dimension.columnSpan} cannot fit into area of column span ${maxColumns}`);
	}

	const tile: TileRenderState<AppType> | undefined = currentState.tiles
		.find((x) => x.data.id === tileId);

	if (!tile) {
		throw new Error(`Tile '${tileId}' not found`);
	}

	const gridState: GridState<TileRenderState> = tileGridSelectors
		.generateStateOfTilesConsumingSpace(currentState);

	// We start from the current visual state
	const area: GridArea = gridState.getAreaForItem(tile);
	area.dimension = { ...dimension };

	if (area.position.column + area.dimension.columnSpan > maxColumns) {
		area.position.column = maxColumns - area.dimension.columnSpan;
	}

	gridState.removeItem(tile)
		.freeUpArea(area)
		.add({
			...area,
			item: tile
		})
		.apply(
			currentState.settings
				.packingAlgorithm
		);

	currentState = applyGridState(
		currentState,
		context,
		gridState
	);

	return persistTileStates(currentState);
}

export function setPlaceholder(
	currentState: TileGridRenderState,
	context: TileGridReducerContext,
	tileId: number,
	placementArea: GridArea
): TileGridRenderState {
	const placeholder: TilePlaceHolderRenderState = {
		...placementArea,
		data: { id: PLACEHOLDER_ID },
		tileId
	};

	if (deepComparison(placeholder, currentState.placeholder)) {
		return currentState;
	}

	currentState = {
		...currentState,
		placeholder
	};

	let gridState: GridState<TileRenderState | TilePlaceHolderRenderState> = tileGridSelectors
		.generateStateOfTilesConsumingSpace(currentState);

	gridState = gridState.freeUpArea(placementArea)
		.add({
			...placementArea,
			item: placeholder
		})
		.apply(
			currentState.settings
				.packingAlgorithm
		);

	return applyGridState(
		currentState,
		context,
		gridState
	);
}

export function clearPlaceholder(
	currentState: TileGridRenderState,
	context: TileGridReducerContext
): TileGridRenderState {
	if (!currentState.placeholder) {
		return currentState;
	}

	currentState = {
		...currentState,
		placeholder: undefined
	};

	const gridState: GridState<TileRenderState> = tileGridSelectors
		.generateStateOfTilesConsumingSpace(currentState);

	return applyGridState(
		currentState,
		context,
		gridState
	);
}

function applyRenderSettingsInternal(
	currentState: PartlyPartial<TileGridRenderState, "dimension" | "settings">,
	context: TileGridReducerContext,
	settings: TileGridRenderSettings
): TileGridRenderState {
	let hasChanges: boolean = false;

	let stateWithoutDimension: PartlyPartial<TileGridRenderState, "dimension">;

	const areTheSameSettings: boolean = deepComparison(currentState.settings, settings);

	if (areTheSameSettings) {
		stateWithoutDimension = currentState as PartlyPartial<TileGridRenderState, "dimension">;
	} else {
		stateWithoutDimension = {
			...currentState,
			settings
		};

		hasChanges = true;
	}

	const tilesWithAdjustedWidth: TileRenderState[] = [];

	for (const currentTileState of stateWithoutDimension.tiles) {
		const resultTileState: TileRenderState = tileReducers.applyTileGridConfiguration(
			currentTileState,
			settings.configuration
		);

		tilesWithAdjustedWidth.push(resultTileState);

		hasChanges ||= currentTileState !== resultTileState;
	}

	if (!hasChanges && isNotNil(stateWithoutDimension.dimension)) {
		return stateWithoutDimension as TileGridRenderState;
	}

	stateWithoutDimension = {
		...stateWithoutDimension,
		tiles: tilesWithAdjustedWidth
	};

	const gridState: GridState<TileRenderState> = tileGridSelectors
		.generateStateOfTilesConsumingSpace(stateWithoutDimension);

	return applyGridState(
		stateWithoutDimension,
		context,
		gridState
	);
}

function applyGridState(
	currentState: PartlyPartial<TileGridRenderState, "dimension">,
	context: TileGridReducerContext,
	gridState: GridState<TileRenderState | TilePlaceHolderRenderState>
): TileGridRenderState {
	const {
		tiles,
		placeholder,
		dimension: originalDimension
	} = currentState;

	const newAreaMap: Map<number | PlaceholderId, GridArea> = fromArray(
		gridState.gridItems,
		(x) => x.item
			.data.id
	);

	const placeholderArea: GridArea | undefined = newAreaMap.get(PLACEHOLDER_ID);

	const newPlaceholder: TilePlaceHolderRenderState | undefined = placeholder && placeholderArea
		? setPropertiesPreservingRefWhenUnchanged(
			placeholder,
			{
				position: placeholderArea.position,
				dimension: placeholderArea.dimension
			}
		)
		: placeholder;

	const updatedTilesBuilder = new ModifiedCollectionBuilder<TileRenderState>(tiles);

	for (let tile of tiles) {
		const area: GridArea | undefined = newAreaMap.get(tile.data.id);

		if (area) {
			tile = tileReducers.setRenderPosition(tile, area.position);
			tile = tileReducers.setRenderDimension(tile, area.dimension);
		}

		updatedTilesBuilder.push(tile);
	}

	const resultDimension: GridDimension = getDimension(
		gridState,
		currentState.settings
			.configuration
			.dimensionLimits
	);

	const hasRowSpanChanged: boolean = !originalDimension
		|| originalDimension.rowSpan !== resultDimension.rowSpan;
	const hasColSpanChanged: boolean = !originalDimension
		|| originalDimension.columnSpan !== resultDimension.columnSpan;

	if (hasRowSpanChanged) {
		context.addEffect(TileGridChangeEffect.HeightIsChanged);
	}

	if (hasRowSpanChanged || hasColSpanChanged) {
		currentState = {
			...currentState,
			dimension: resultDimension
		};
	}

	return setPropertiesPreservingRefWhenUnchanged(
		currentState as TileGridRenderState,
		{
			tiles: updatedTilesBuilder.result(),
			placeholder: newPlaceholder
		}
	);
}

export function persistTileStates(currentState: TileGridRenderState): TileGridRenderState {
	const newTiles = new ModifiedCollectionBuilder<TileRenderState>(currentState.tiles);

	for (let tile of currentState.tiles) {
		tile = tileReducers.persistRenderPosition(tile);
		tile = tileReducers.persistRenderDimension(tile);

		newTiles.push(tile);
	}

	return setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			tiles: newTiles.result()
		}
	);
}

function getDimension(
	gridState: GridState<TileRenderState | TilePlaceHolderRenderState>,
	dimensionLimits: TileGridDimensionLimits
): GridDimension {
	const gridStateDimension: GridDimension = gridState.dimension;

	return {
		columnSpan: Math.max(dimensionLimits.columns, gridStateDimension.columnSpan),
		rowSpan: Math.max(dimensionLimits.minRows, gridStateDimension.rowSpan)
	};
}